import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CampaignIcon from "@mui/icons-material/Campaign";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import NotificationsIcon from "@mui/icons-material/Notifications";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TuneIcon from "@mui/icons-material/Tune";

import api from "../../../services/api/axios";
import { useAuth } from "../../../providers/AuthProvider";
import useModules from "../../../hooks/useModules";
import { PRIMARY_BUTTON_SX } from "../../../theme/buttonstyles";
import { US_STATES } from "../../../utils/States";
import { COUNTRY_MAP } from "../../../utils/Countries";
import { AUTOCOMPLETE_DROPDOWN_SLOT_PROPS } from "../../../theme/filterstyles";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const FILTER_STORAGE_KEY = "prospect_generator_filters";
const DAY_OPTIONS = [
  { value: "monday", label: "Monday", short: "Mon" },
  { value: "tuesday", label: "Tuesday", short: "Tue" },
  { value: "wednesday", label: "Wednesday", short: "Wed" },
  { value: "thursday", label: "Thursday", short: "Thu" },
  { value: "friday", label: "Friday", short: "Fri" },
  { value: "saturday", label: "Saturday", short: "Sat" },
  { value: "sunday", label: "Sunday", short: "Sun" },
];
export const SIGNAL_OPTIONS = [
  {
    code: "expansion_investment",
    name: "Expansion, Investment & New Projects",
  },
  {
    code: "hiring_workforce_growth",
    name: "Hiring & Workforce Growth",
  },
  {
    code: "contracts_partnerships",
    name: "Contracts & Partnerships",
  },
  {
    code: "mergers_acquisitions",
    name: "Mergers & Acquisitions",
  },
  {
    code: "revenue_fast_growth",
    name: "Revenue & Fast Growth",
  },
];
const SIGNAL_CODE_ALIASES = {
  facility_expansion_announcements: "expansion_investment",
  expansion_announcements: "expansion_investment",
  funding_rounds: "expansion_investment",
  investment_announcements: "expansion_investment",
  new_project_announcements: "expansion_investment",
  hiring_surges: "hiring_workforce_growth",
  major_contract_wins: "contracts_partnerships",
  partnership_announcements: "contracts_partnerships",
  federal_contract_awards: "contracts_partnerships",
  acquisition_announcements: "mergers_acquisitions",
  revenue_milestones: "revenue_fast_growth",
};
const canonicalSignalCode = (code) => SIGNAL_CODE_ALIASES[code] || code;
const uniqueSignalCodes = (codes = []) => [
  ...new Set((codes || []).map(canonicalSignalCode).filter(Boolean)),
];
const REGION_DEFINITIONS = [
  {
    code: "region-northeast",
    name: "Northeast Region",
    type: "region",
    group: "Regions",
    states: [
      "Connecticut",
      "Maine",
      "Massachusetts",
      "New Hampshire",
      "New Jersey",
      "New York",
      "Pennsylvania",
      "Rhode Island",
      "Vermont",
    ],
  },
  {
    code: "region-southeast",
    name: "Southeast Region",
    type: "region",
    group: "Regions",
    states: [
      "Alabama",
      "Arkansas",
      "Florida",
      "Georgia",
      "Kentucky",
      "Louisiana",
      "Mississippi",
      "North Carolina",
      "South Carolina",
      "Tennessee",
      "Virginia",
      "West Virginia",
    ],
  },
  {
    code: "region-midwest",
    name: "Midwest Region",
    type: "region",
    group: "Regions",
    states: [
      "Illinois",
      "Indiana",
      "Iowa",
      "Kansas",
      "Michigan",
      "Minnesota",
      "Missouri",
      "Nebraska",
      "North Dakota",
      "Ohio",
      "South Dakota",
      "Wisconsin",
    ],
  },
  {
    code: "region-southwest",
    name: "Southwest Region",
    type: "region",
    group: "Regions",
    states: ["Arizona", "New Mexico", "Oklahoma", "Texas"],
  },
  {
    code: "region-west",
    name: "West Region",
    type: "region",
    group: "Regions",
    states: [
      "Alaska",
      "California",
      "Colorado",
      "Hawaii",
      "Idaho",
      "Montana",
      "Nevada",
      "Oregon",
      "Utah",
      "Washington",
      "Wyoming",
    ],
  },
  {
    code: "region-great-lakes",
    name: "Great Lakes Region",
    type: "region",
    group: "Regions",
    states: [
      "Illinois",
      "Indiana",
      "Michigan",
      "Minnesota",
      "New York",
      "Ohio",
      "Pennsylvania",
      "Wisconsin",
    ],
  },
];
const EMPLOYEE_RANGE_OPTIONS = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001+",
];
const COUNTRY_OPTIONS = Object.values(COUNTRY_MAP).sort((a, b) =>
  a.localeCompare(b),
);

const filterSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: 3,
    backgroundColor: "#ffffff",
    "& fieldset": {
      borderColor: "rgba(0, 51, 108, 0.25)",
      borderWidth: 1.5,
    },
    "&:hover fieldset": {
      borderColor: BRAND_BLUE,
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND_BLUE,
      borderWidth: 2,
    },
  },
};

const CHIP_SX = {
  backgroundColor: BRAND_BLUE,
  color: "white",
  fontWeight: 600,
  height: 22,
  "& .MuiChip-label": { px: 0.75 },
  "& .MuiChip-deleteIcon": {
    color: "white",
    "&:hover": { color: "white", opacity: 0.85 },
  },
};

function deriveDomain(website) {
  if (!website) return "";
  try {
    const normalized = website.startsWith("http")
      ? website
      : `https://${website}`;
    return new URL(normalized).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

function CompanyAvatar({ name }) {
  const initial = name ? name.charAt(0).toUpperCase() : "?";
  const hue = name
    ? (name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % 6) * 60
    : 0;
  const colors = [
    { bg: "rgba(0, 51, 108, 0.12)", color: BRAND_BLUE },
    { bg: "rgba(100, 131, 21, 0.12)", color: BRAND_GREEN },
    { bg: "rgba(14, 116, 144, 0.12)", color: "#0e7490" },
    { bg: "rgba(124, 58, 237, 0.1)", color: "#7c3aed" },
    { bg: "rgba(217, 119, 6, 0.1)", color: "#d97706" },
    { bg: "rgba(220, 38, 38, 0.1)", color: "#dc2626" },
  ];
  const { bg, color } = colors[Math.floor(hue / 60) % colors.length];
  return (
    <Avatar
      sx={{
        width: 44,
        height: 44,
        backgroundColor: bg,
        color,
        fontWeight: 800,
        fontSize: "1.1rem",
        flexShrink: 0,
      }}
    >
      {initial}
    </Avatar>
  );
}

function ProspectCardSkeleton() {
  return (
    <Card
      sx={{
        borderRadius: 3,
        border: `1px solid rgba(0, 51, 108, 0.1)`,
        boxShadow: "0 4px 16px rgba(0, 51, 108, 0.06)",
        height: "100%",
        minHeight: 240,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Skeleton variant="circular" width={44} height={44} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="70%" height={22} />
              <Skeleton variant="text" width="40%" height={18} />
            </Box>
          </Stack>
          <Skeleton variant="text" width="55%" height={18} />
          <Skeleton variant="text" width="40%" height={18} />
          <Skeleton variant="rounded" width="100%" height={16} />
          <Skeleton variant="rounded" width="80%" height={16} />
          <Skeleton variant="rounded" width="60%" height={16} />
          <Skeleton variant="rounded" width="100%" height={38} sx={{ mt: 1 }} />
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function ProspectGenerator() {
  const navigate = useNavigate();
  const location = useLocation();
  const { authReady, token, user, setUser } = useAuth();
  const { hasComponent } = useModules();
  const canAddToProspecting = hasComponent("prospecting.companies");
  const [prospectsOpen, setProspectsOpen] = useState(false);
  const [selectedSectors, setSelectedSectors] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedSignals, setSelectedSignals] = useState([]);
  const [selectedEmployeeRanges, setSelectedEmployeeRanges] = useState([]);
  const [generatedProspects, setGeneratedProspects] = useState([]);
  const [scheduledProspectsByWeek, setScheduledProspectsByWeek] = useState({
    0: [],
    1: [],
  });
  const [scheduledWeekOffset, setScheduledWeekOffset] = useState(0);
  const [prospectListMode, setProspectListMode] = useState("generated");
  const [hasGenerated, setHasGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [scheduledLoadingWeekOffset, setScheduledLoadingWeekOffset] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDays, setScheduleDays] = useState([]);
  const [addingIds, setAddingIds] = useState([]);
  const [addedIds, setAddedIds] = useState([]);
  const [error, setError] = useState("");
  const [scheduleError, setScheduleError] = useState("");
  const [targetProfile, setTargetProfile] = useState(null);
  const [targetProfileOpen, setTargetProfileOpen] = useState(false);
  const [targetProfileSaving, setTargetProfileSaving] = useState(false);
  const [targetProfileError, setTargetProfileError] = useState("");
  const [selectedTargetLocations, setSelectedTargetLocations] = useState([]);
  const [selectedTargetCountries, setSelectedTargetCountries] = useState([]);
  const [naicsSectorOptions, setNaicsSectorOptions] = useState([]);

  const locationOptions = useMemo(
    () => [
      ...REGION_DEFINITIONS,
      ...US_STATES.map((item) => ({
        code: item.code,
        name: item.name,
        label: `${item.name} (${item.code})`,
        type: "state",
        group: "States",
        states: [item.name],
      })),
    ],
    [],
  );
  const targetGeographyOptions = useMemo(
    () => [
      {
        code: "country-United States",
        name: "United States",
        label: "United States",
        type: "country",
        group: "United States",
      },
      ...locationOptions.map((option) => ({
        ...option,
        group: option.type === "region" ? "Regions" : "States",
      })),
      ...COUNTRY_OPTIONS.filter((name) => name !== "United States").map((name) => ({
        code: `country-${name}`,
        name,
        label: name,
        type: "country",
        group: "Countries",
      })),
    ],
    [locationOptions],
  );
  const selectedTargetGeographies = useMemo(
    () => [
      ...selectedTargetCountries.map((name) => ({
        code: `country-${name}`,
        name,
        label: name,
        type: "country",
        group: name === "United States" ? "United States" : "Countries",
      })),
      ...selectedTargetLocations.map((option) => ({
        ...option,
        group: option.type === "region" ? "Regions" : "States",
      })),
    ],
    [selectedTargetCountries, selectedTargetLocations],
  );
  const targetProfileSectorOptions = useMemo(
    () => [
      ...new Set([
        ...naicsSectorOptions,
        ...((targetProfile?.preferred_sectors || []).filter(Boolean)),
      ]),
    ],
    [naicsSectorOptions, targetProfile?.preferred_sectors],
  );
  const selectedTargetGrowthSignals = useMemo(
    () =>
      uniqueSignalCodes(targetProfile?.preferred_growth_signals || [])
        .map(
          (code) =>
            SIGNAL_OPTIONS.find((option) => option.code === code) || null,
        )
        .filter(Boolean),
    [targetProfile?.preferred_growth_signals],
  );

  useEffect(() => {
    const incomingDays = Array.isArray(user?.prospect_report_days)
      ? user.prospect_report_days
      : [];

    setScheduleDays(incomingDays);
    setScheduleEnabled(incomingDays.length > 0);

    if (
      Array.isArray(user?.prospect_report_sectors) &&
      user.prospect_report_sectors.length
    ) {
      setSelectedSectors(user.prospect_report_sectors);
    }

    if (
      Array.isArray(user?.prospect_report_signals) &&
      user.prospect_report_signals.length
    ) {
      setSelectedSignals(
        uniqueSignalCodes(user.prospect_report_signals)
          .map(
            (code) =>
              SIGNAL_OPTIONS.find((option) => option.code === code) || null,
          )
          .filter(Boolean),
      );
    }

    if (
      Array.isArray(user?.prospect_report_location_codes) &&
      user.prospect_report_location_codes.length
    ) {
      setSelectedLocations(
        user.prospect_report_location_codes
          .map(
            (code) =>
              locationOptions.find((option) => option.code === code) || null,
          )
          .filter(Boolean),
      );
    }
  }, [locationOptions, user]);

  useEffect(() => {
    if (!authReady || !token) return;
    api.get("/reference/naics/sectors", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        const options = [
          ...new Set(
            (Array.isArray(res.data) ? res.data : [])
              .map((item) => item?.display_label)
              .filter(Boolean),
          ),
        ];
        setNaicsSectorOptions(options);
      })
      .catch(() => setNaicsSectorOptions([]));
  }, [authReady, token]);

  useEffect(() => {
    if (!authReady || !token) return;
    api.get("/prospecting-target-profile", {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setTargetProfile(res.data);
        const stateNames = new Set(res.data?.target_states || []);
        const marketNames = new Set(
          (res.data?.target_markets || []).map((name) => name.toLowerCase()),
        );
        setSelectedTargetLocations(
          locationOptions.filter((option) =>
            option.type === "state"
              ? stateNames.has(option.name)
              : marketNames.has(
                  option.name.replace(/ Region$/i, "").toLowerCase(),
                ),
          ),
        );
        setSelectedTargetCountries(
          Array.isArray(res.data?.target_countries)
            ? res.data.target_countries
            : [],
        );
      })
      .catch(() => setTargetProfileError("Unable to load the organization target profile."));
  }, [authReady, token, locationOptions]);

  const updateTargetProfile = (field, value) => {
    setTargetProfile((current) => ({ ...(current || {}), [field]: value }));
  };

  const updateTargetGeographies = (value) => {
    const selectedCountries = value
      .filter((item) => item.type === "country")
      .map((item) => item.name);
    const selectedUsLocations = value.filter((item) => item.type !== "country");
    const nextCountries =
      selectedUsLocations.length > 0 && !selectedCountries.includes("United States")
        ? [...selectedCountries, "United States"]
        : selectedCountries;
    const nextLocations = nextCountries.includes("United States")
      ? selectedUsLocations
      : [];

    setSelectedTargetCountries(nextCountries);
    setSelectedTargetLocations(nextLocations);
    updateTargetProfile("target_countries", nextCountries);
    updateTargetProfile(
      "target_states",
      nextLocations.filter((item) => item.type === "state").map((item) => item.name),
    );
    updateTargetProfile(
      "target_markets",
      nextLocations
        .filter((item) => item.type === "region")
        .map((item) => item.name.replace(/ Region$/i, "")),
    );
  };

  const saveTargetProfile = async () => {
    try {
      setTargetProfileSaving(true);
      setTargetProfileError("");
      const payload = {
        preferred_sectors: targetProfile?.preferred_sectors || [],
        preferred_naics_codes: [],
        preferred_growth_signals: uniqueSignalCodes(targetProfile?.preferred_growth_signals || []),
        target_countries: targetProfile?.target_countries || [],
        target_states: targetProfile?.target_states || [],
        target_markets: targetProfile?.target_markets || [],
        preferred_employee_ranges: targetProfile?.preferred_employee_ranges || [],
      };
      const res = await api.put("/prospecting-target-profile", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTargetProfile(res.data);
    } catch (err) {
      console.error("Failed to save prospecting target profile", err);
      setTargetProfileError("Unable to save the organization target profile.");
    } finally {
      setTargetProfileSaving(false);
    }
  };

  useEffect(() => {
    try {
      const stored = JSON.parse(
        localStorage.getItem(FILTER_STORAGE_KEY) || "{}",
      );
      if (Array.isArray(stored.selectedSectors)) {
        setSelectedSectors(stored.selectedSectors.filter(Boolean));
      }
      if (Array.isArray(stored.selectedSignals)) {
        setSelectedSignals(
          stored.selectedSignals
            .filter(Boolean)
            .map((item) => {
              const code = canonicalSignalCode(typeof item === "string" ? item : item?.code);
              return (
                SIGNAL_OPTIONS.find((option) => option.code === code) || null
              );
            })
            .filter(Boolean),
        );
      }
      if (Array.isArray(stored.selectedLocations)) {
        setSelectedLocations(
          stored.selectedLocations
            .filter(Boolean)
            .map((item) => {
              const code = typeof item === "string" ? item : item?.code;
              if (!code) return null;
              const match = locationOptions.find(
                (option) => option.code === code,
              );
              if (match) {
                return match;
              }
              return null;
            })
            .filter(Boolean),
        );
      }
      if (Array.isArray(stored.selectedEmployeeRanges)) {
        setSelectedEmployeeRanges(
          stored.selectedEmployeeRanges.filter((item) =>
            EMPLOYEE_RANGE_OPTIONS.includes(item),
          ),
        );
      }
    } catch {
      localStorage.removeItem(FILTER_STORAGE_KEY);
    }
  }, [locationOptions]);

  useEffect(() => {
    try {
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({
          selectedSectors,
          selectedSignals: selectedSignals.map((item) => ({
            code: item.code,
          })),
          selectedLocations: selectedLocations.map((item) => ({
            code: item.code,
          })),
          selectedEmployeeRanges,
        }),
      );
    } catch {}
  }, [selectedEmployeeRanges, selectedLocations, selectedSectors, selectedSignals]);

  const refreshScheduledProspects = useCallback(async (weekOffset = 0) => {
    try {
      if (!token) return;
      setScheduledLoadingWeekOffset(weekOffset);

      const res = await api.get("/weekly_prospects", {
        params: { week_offset: weekOffset },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setScheduledProspectsByWeek((current) => ({
        ...current,
        [weekOffset]: Array.isArray(res.data) ? res.data : [],
      }));
    } catch (err) {
      console.error("Failed to refresh scheduled prospects", err);
    } finally {
      setScheduledLoadingWeekOffset((current) =>
        current === weekOffset ? null : current,
      );
    }
  }, [token]);

  const openScheduledProspects = useCallback(async (weekOffset) => {
    setScheduledWeekOffset(weekOffset);
    setProspectListMode("scheduled");
    setProspectsOpen(true);
    await refreshScheduledProspects(weekOffset);
  }, [refreshScheduledProspects]);

  const scheduleSummary = useMemo(() => {
    if (!scheduleEnabled || scheduleDays.length === 0) {
      return "Prospect list reports are currently off";
    }

    const labels = DAY_OPTIONS.filter((day) =>
      scheduleDays.includes(day.value),
    ).map((day) => day.label);

    return `${labels.join(", ")} at 9:00 AM`;
  }, [scheduleDays, scheduleEnabled]);

  const handleGenerate = async () => {
    try {
      setLoading(true);
      setError("");
      setHasGenerated(true);
      setAddedIds([]);

      const res = await api.get("/prospects/generate", {
        params: {
          limit: 8,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 300000,
      });

      setGeneratedProspects(Array.isArray(res.data) ? res.data : []);
      setProspectListMode("generated");
      setProspectsOpen(true);
      refreshScheduledProspects(0);
    } catch (err) {
      console.error("Failed to generate prospects", err);
      setGeneratedProspects([]);
      const isTimeout = err?.code === "ECONNABORTED" || err?.code === "ERR_CANCELED";
      setError(
        isTimeout
          ? "The AI search is taking longer than expected. Please try again in a moment."
          : err?.response?.data?.detail || "Unable to generate prospects right now.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authReady || !token) return;

    refreshScheduledProspects();

    const timer = window.setInterval(() => {
      refreshScheduledProspects();
    }, 60000);

    return () => window.clearInterval(timer);
  }, [authReady, token, refreshScheduledProspects]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("open_weekly_prospects") !== "1") {
      return;
    }

    openScheduledProspects(0);

    searchParams.delete("open_weekly_prospects");
    navigate(
      {
        pathname: location.pathname,
        search: searchParams.toString() ? `?${searchParams.toString()}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, openScheduledProspects]);

  const handleAddProspect = async (prospect) => {
    try {
      if (!canAddToProspecting) {
        setError("You do not have access to add companies to prospecting.");
        return;
      }

      setAddingIds((prev) => [...prev, prospect.id]);

      await api.post(
        "/prospecting-companies/",
        {
          name: prospect.name,
          city: prospect.city || null,
          state: prospect.state || null,
          country: "United States",
          number_of_employees: prospect.number_of_employees ?? null,
          website: prospect.website || null,
          domain: deriveDomain(prospect.website),
          description: null,
          sector: prospect.sector || null,
          naics_code: prospect.naics_code || null,
          naics_name: prospect.naics_name || null,
          naics_extra: prospect.naics_extra || null,
          data_source: "AI Prospect Generator",
          lead_score: prospect.score ?? null,
          confidence_score: prospect.score ?? null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      setAddedIds((prev) =>
        prev.includes(prospect.id) ? prev : [...prev, prospect.id],
      );
    } catch (err) {
      console.error("Failed to add prospecting company", err);
      setError("Unable to add that company to prospecting right now.");
    } finally {
      setAddingIds((prev) => prev.filter((id) => id !== prospect.id));
    }
  };

  const visibleProspects =
    prospectListMode === "scheduled"
      ? scheduledProspectsByWeek[scheduledWeekOffset] || []
      : generatedProspects;
  const resultsLoading =
    prospectListMode === "scheduled"
      ? scheduledLoadingWeekOffset === scheduledWeekOffset
      : loading;
  const thisWeekProspectCount = scheduledProspectsByWeek[0]?.length || 0;
  const showEmptyGeneratedState =
    prospectListMode === "generated" &&
    !resultsLoading &&
    hasGenerated &&
    visibleProspects.length === 0;
  const showEmptyScheduledState =
    prospectListMode === "scheduled" &&
    !resultsLoading &&
    visibleProspects.length === 0;
  const resultsTitle =
    prospectListMode === "scheduled"
      ? scheduledWeekOffset === 1
        ? "Last Week's Prospect Lists"
        : "This Week's Prospect Lists"
      : "Prospect List";
  const resultsSubtitle =
    prospectListMode === "scheduled"
      ? `Review all manually generated and automated prospect results saved for ${scheduledWeekOffset === 1 ? "last week" : "the current week"}`
      : "Review AI-generated companies and add the strongest matches to prospecting";

  const handleToggleScheduleDay = (dayValue) => {
    setScheduleError("");
    setScheduleDays((prev) => {
      if (prev.includes(dayValue)) {
        return prev.filter((day) => day !== dayValue);
      }
      if (prev.length >= 2) {
        setScheduleError("You can choose up to 2 report days.");
        return prev;
      }
      return [...prev, dayValue];
    });
  };

  const handleSaveSchedule = async () => {
    try {
      setScheduleSaving(true);
      setScheduleError("");

      const daysToSave = scheduleEnabled ? scheduleDays : [];

      if (scheduleEnabled && daysToSave.length === 0) {
        setScheduleError("Select at least 1 day when scheduling is enabled.");
        return;
      }

      const res = await api.patch(
        "/auth/me/prospect-report-schedule",
        {
          prospect_report_days: daysToSave,
          prospect_report_sectors: [],
          prospect_report_location_codes: [],
          prospect_report_states: [],
          prospect_report_signals: [],
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (res.data) {
        setUser((current) => ({ ...(current || {}), ...res.data }));
      }
    } catch (err) {
      console.error("Failed to save prospect report schedule", err);
      setScheduleError("Unable to save prospect report schedule right now.");
    } finally {
      setScheduleSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          border: "1px solid #dce4ef", borderRadius: 2.5, backgroundColor: "#ffffff",
          boxShadow: "0 2px 16px rgba(0, 51, 108, 0.06)", overflow: "hidden",
        }}
      >
        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.75, display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 16 }}>Organization Target Profile</Typography>
            <Typography sx={{ color: "#6b85a1", fontSize: 13, mt: 0.25 }}>Reusable strategy used to guide and score AI prospect lists.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setTargetProfileOpen((open) => !open)} sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}>
            {targetProfileOpen ? "Hide profile" : "Configure profile"}
          </Button>
        </Box>
        {targetProfileOpen ? (
          <Box sx={{ borderTop: "1px solid #eef2f8", p: { xs: 2, md: 3 } }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              When a search filter is left empty, the saved profile supplies the target sectors, growth signals, geographies, and company sizes. Explicit search filters still take precedence.
            </Alert>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 1.5 }}>
              <Autocomplete
                multiple
                limitTags={1}
                size="small"
                options={targetProfileSectorOptions}
                value={targetProfile?.preferred_sectors || []}
                onChange={(_event, value) => updateTargetProfile("preferred_sectors", value)}
                disableCloseOnSelect
                slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
                renderTags={(value, getTagProps) =>
                  !value.length
                    ? null
                    : [
                        <Chip
                          {...getTagProps({ index: 0 })}
                          key={value[0]}
                          size="small"
                          label={
                            value.length > 1
                              ? `${value[0]} +${value.length - 1}`
                              : value[0]
                          }
                          sx={CHIP_SX}
                        />,
                      ]
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Preferred sectors"
                    placeholder={
                      !targetProfile?.preferred_sectors?.length
                        ? "Select NAICS sectors"
                        : ""
                    }
                    size="small"
                    helperText="Uses the standard ProspectEngage sector buckets"
                  />
                )}
                sx={filterSx}
              />
              <Autocomplete
                multiple
                limitTags={1}
                size="small"
                options={SIGNAL_OPTIONS}
                value={selectedTargetGrowthSignals}
                onChange={(_event, value) =>
                  updateTargetProfile(
                    "preferred_growth_signals",
                    value.map((item) => item.code),
                  )
                }
                disableCloseOnSelect
                slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderTags={(value, getTagProps) =>
                  !value.length
                    ? null
                    : [
                        <Chip
                          {...getTagProps({ index: 0 })}
                          key={value[0].code}
                          size="small"
                          label={
                            value.length > 1
                              ? `${value[0].name} +${value.length - 1}`
                              : value[0].name
                          }
                          sx={CHIP_SX}
                        />,
                      ]
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Growth signals"
                    placeholder={
                      selectedTargetGrowthSignals.length === 0
                        ? "Any growth signal"
                        : ""
                    }
                    size="small"
                    helperText=" "
                  />
                )}
                sx={filterSx}
              />
              <Autocomplete
                multiple
                limitTags={1}
                size="small"
                options={EMPLOYEE_RANGE_OPTIONS}
                value={targetProfile?.preferred_employee_ranges || []}
                onChange={(_event, value) => updateTargetProfile("preferred_employee_ranges", value)}
                disableCloseOnSelect
                slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
                renderTags={(value, getTagProps) =>
                  !value.length
                    ? null
                    : [
                        <Chip
                          {...getTagProps({ index: 0 })}
                          key={value[0]}
                          size="small"
                          label={
                            value.length > 1
                              ? `${value[0]} +${value.length - 1}`
                              : value[0]
                          }
                          sx={CHIP_SX}
                        />,
                      ]
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Preferred employee ranges"
                    placeholder={
                      !targetProfile?.preferred_employee_ranges?.length
                        ? "Any employee range"
                        : ""
                    }
                    size="small"
                    helperText=" "
                  />
                )}
                sx={filterSx}
              />
              <Autocomplete
                multiple
                limitTags={1}
                size="small"
                options={targetGeographyOptions}
                value={selectedTargetGeographies}
                onChange={(_event, value) => updateTargetGeographies(value)}
                disableCloseOnSelect
                slotProps={AUTOCOMPLETE_DROPDOWN_SLOT_PROPS}
                groupBy={(option) => option.group}
                getOptionLabel={(option) => option.label || option.name}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                renderTags={(value, getTagProps) =>
                  !value.length
                    ? null
                    : [
                        <Chip
                          {...getTagProps({ index: 0 })}
                          key={value[0].code}
                          size="small"
                          label={
                            value.length > 1
                              ? `${value[0].name} +${value.length - 1}`
                              : value[0].name
                          }
                          sx={CHIP_SX}
                        />,
                      ]
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Target geographies"
                    placeholder={selectedTargetGeographies.length === 0 ? "Any geography" : ""}
                    size="small"
                    helperText="Countries, US states, and multi-state markets"
                  />
                )}
                sx={filterSx}
              />
            </Box>
            {targetProfileError ? <Alert severity="error" sx={{ mt: 2 }}>{targetProfileError}</Alert> : null}
            <Button variant="contained" onClick={saveTargetProfile} disabled={!targetProfile || targetProfileSaving} sx={{ ...PRIMARY_BUTTON_SX, mt: 2, textTransform: "none" }}>
              {targetProfileSaving ? "Saving..." : "Save target profile"}
            </Button>
          </Box>
        ) : null}
        <Box
          sx={{
            borderTop: "1px solid #eef2f8",
            px: { xs: 2, md: 3 },
            py: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography
                sx={{ color: BRAND_BLUE, fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}
              >
                Automated Reports
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#6b85a1", mt: 0.3, display: "block" }}
              >
                Uses the saved organization profile to generate recurring prospect lists. {scheduleSummary}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={scheduleEnabled}
                  onChange={(event) => {
                    setScheduleEnabled(event.target.checked);
                    setScheduleError("");
                  }}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: BRAND_GREEN },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: BRAND_GREEN,
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND_BLUE }}>
                  {scheduleEnabled ? "On" : "Off"}
                </Typography>
              }
              labelPlacement="start"
              sx={{ mr: 0 }}
            />
          </Box>

          {scheduleEnabled && (
            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #eef2f8" }}>
              <Typography
                variant="caption"
                sx={{ display: "block", color: BRAND_BLUE, fontWeight: 700, mb: 1 }}
              >
                Report Days{" "}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#6b85a1", fontWeight: 400 }}
                >
                  (select up to 2)
                </Typography>
              </Typography>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                {DAY_OPTIONS.map((day) => {
                  const isSelected = scheduleDays.includes(day.value);
                  const isDisabled = !isSelected && scheduleDays.length >= 2;
                  return (
                    <Box
                      key={day.value}
                      component="button"
                      onClick={() => !isDisabled && handleToggleScheduleDay(day.value)}
                      sx={{
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        border: isSelected
                          ? `2px solid ${BRAND_BLUE}`
                          : "2px solid #e0e8f0",
                        outline: "none",
                        borderRadius: "20px",
                        px: 1.75,
                        py: 0.5,
                        fontFamily: "inherit",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        transition: "all 0.15s ease",
                        backgroundColor: isSelected ? BRAND_BLUE : "#ffffff",
                        color: isSelected
                          ? "#ffffff"
                          : isDisabled
                          ? "rgba(0,0,0,0.26)"
                          : BRAND_BLUE,
                        boxShadow: isSelected
                          ? "0 2px 8px rgba(0, 51, 108, 0.2)"
                          : "none",
                        "&:hover": isDisabled
                          ? {}
                          : {
                              backgroundColor: isSelected ? "#002a59" : "rgba(0, 51, 108, 0.06)",
                              borderColor: isSelected ? "#002a59" : BRAND_BLUE,
                            },
                      }}
                    >
                      {day.short}
                    </Box>
                  );
                })}
              </Stack>
              <Typography
                variant="caption"
                sx={{ color: "#6b85a1", display: "block", mb: 2 }}
              >
                Reports are sent at 9:00 AM on each selected day.
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Button
                  variant="contained"
                  onClick={handleSaveSchedule}
                  disabled={scheduleSaving}
                  sx={{ ...PRIMARY_BUTTON_SX, borderRadius: 2, height: 38 }}
                >
                  {scheduleSaving ? "Saving..." : "Save Schedule"}
                </Button>
                {scheduleError ? (
                  <Typography variant="caption" sx={{ color: "error.main", fontWeight: 600 }}>
                    {scheduleError}
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
      {/* Prospect list actions */}
      <Box
        sx={{
          border: "1px solid #dce4ef",
          borderRadius: 2.5,
          backgroundColor: "#ffffff",
          boxShadow: "0 2px 16px rgba(0, 51, 108, 0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 2,
            background: "linear-gradient(135deg, #fafcff 0%, #f3f7fd 100%)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="contained"
              startIcon={
                loading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <AutoAwesomeIcon />
                )
              }
              onClick={handleGenerate}
              disabled={!authReady || !token || loading}
              sx={{
                ...PRIMARY_BUTTON_SX,
                height: 42,
                borderRadius: 2.5,
                boxShadow: "0 4px 14px rgba(100, 131, 21, 0.3)",
                minWidth: 200,
                fontWeight: 700,
              }}
            >
              {loading ? "Generating..." : "Generate Prospect List"}
            </Button>

            <Badge
              badgeContent={thisWeekProspectCount}
              color="success"
              invisible={thisWeekProspectCount === 0}
              sx={{
                alignSelf: { xs: "flex-start", sm: "center" },
                "& .MuiBadge-badge": { fontWeight: 700 },
              }}
            >
              <Button
                variant="outlined"
                startIcon={<NotificationsIcon />}
                onClick={() => openScheduledProspects(0)}
                sx={{
                  height: 42,
                  minWidth: 200,
                  borderRadius: 2.5,
                  borderWidth: 1.5,
                  color: BRAND_BLUE,
                  borderColor: "#c5d4e8",
                  fontWeight: 600,
                  backgroundColor: "#ffffff",
                  "&:hover": {
                    borderWidth: 1.5,
                    borderColor: BRAND_BLUE,
                    backgroundColor: "rgba(0, 51, 108, 0.04)",
                  },
                }}
              >
                This Week's Lists
              </Button>
            </Badge>
            <Button
              variant="outlined"
              startIcon={<NotificationsIcon />}
              onClick={() => openScheduledProspects(1)}
              sx={{
                height: 42, minWidth: 200, borderRadius: 2.5, borderWidth: 1.5,
                color: BRAND_BLUE, borderColor: "#c5d4e8", fontWeight: 600, backgroundColor: "#ffffff",
                "&:hover": { borderWidth: 1.5, borderColor: BRAND_BLUE, backgroundColor: "rgba(0, 51, 108, 0.04)" },
              }}
            >
              Last Week's Lists
            </Button>
          </Stack>
        </Box>
      </Box>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {!canAddToProspecting ? (
        <Alert severity="info">
          You can generate and review prospect lists, but adding companies
          requires the <strong>Prospecting Companies</strong> component.
        </Alert>
      ) : null}

      {prospectsOpen ||
      resultsLoading ||
      showEmptyGeneratedState ||
      visibleProspects.length > 0 ? (
        <Box
          sx={{
            border: "1px solid #dce4ef",
            borderRadius: 2.5,
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 16px rgba(0, 51, 108, 0.06)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 2.5,
              background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1a4f8a 100%)`,
              color: "#ffffff",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, letterSpacing: 0 }}
            >
              {resultsTitle}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.8, fontSize: 13.5 }}>
              {resultsSubtitle}
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f4f7fc" }}>
            {/* Skeleton loading */}
            {resultsLoading ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2.5,
                }}
              >
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <ProspectCardSkeleton key={i} />
                ))}
              </Box>
            ) : null}

            {/* Empty state */}
            {showEmptyGeneratedState ? (
              <Box
                sx={{
                  borderRadius: 3,
                  border: `1px dashed rgba(0, 51, 108, 0.2)`,
                  backgroundColor: "#ffffff",
                  px: 3,
                  py: 6,
                  textAlign: "center",
                }}
              >
                <AutoAwesomeIcon
                  sx={{ fontSize: 40, color: "rgba(0,51,108,0.18)", mb: 1.5 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: BRAND_BLUE, fontWeight: 700 }}
                >
                  No companies found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#6b85a1", mt: 0.5 }}
                >
                  Try broadening the saved Organization Target Profile.
                </Typography>
              </Box>
            ) : null}

            {showEmptyScheduledState ? (
              <Box
                sx={{
                  borderRadius: 3,
                  border: `1px dashed rgba(0, 51, 108, 0.2)`,
                  backgroundColor: "#ffffff",
                  px: 3,
                  py: 6,
                  textAlign: "center",
                }}
              >
                <NotificationsIcon
                  sx={{ fontSize: 40, color: "rgba(0,51,108,0.18)", mb: 1.5 }}
                />
                <Typography
                  variant="body1"
                  sx={{ color: BRAND_BLUE, fontWeight: 700 }}
                >
                  No prospect lists found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#6b85a1", mt: 0.5 }}
                >
                  There are no saved prospects for {scheduledWeekOffset === 1 ? "last week" : "this week"}.
                </Typography>
              </Box>
            ) : null}

            {/* Prospect cards */}
            {!resultsLoading && visibleProspects.length > 0 ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 2,
                  alignItems: "stretch",
                }}
              >
                {visibleProspects.map((prospect) => {
                  const isAdding = addingIds.includes(prospect.id);
                  const isAdded = addedIds.includes(prospect.id);
                  const hasScoreBreakdown = Object.keys(
                    prospect.score_breakdown || {},
                  ).length > 0;
                  return (
                    <Card
                      key={prospect.id}
                      sx={{
                        borderRadius: 3,
                        border: isAdded
                          ? `1.5px solid rgba(100, 131, 21, 0.4)`
                          : `1px solid rgba(0, 51, 108, 0.1)`,
                        boxShadow: "0 4px 16px rgba(0, 51, 108, 0.07)",
                        display: "flex",
                        flexDirection: "column",
                        transition: "transform 0.18s ease, box-shadow 0.18s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 10px 28px rgba(0, 51, 108, 0.13)",
                        },
                        backgroundColor: isAdded
                          ? "rgba(100, 131, 21, 0.03)"
                          : "#ffffff",
                      }}
                    >
                      <CardContent
                        sx={{
                          p: 2.25,
                          display: "flex",
                          flexDirection: "column",
                          flexGrow: 1,
                          "&:last-child": { pb: 2.25 },
                        }}
                      >
                        {/* Header: avatar + name + sector */}
                        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.75 }}>
                          <CompanyAvatar name={prospect.name} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: BRAND_BLUE,
                                fontWeight: 800,
                                lineHeight: 1.2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {prospect.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: BRAND_GREEN, fontWeight: 700 }}
                            >
                              {prospect.sector || "Sector not available"}
                            </Typography>
                          </Box>
                        </Stack>

                        {prospect.score != null && hasScoreBreakdown ? (
                          <Box sx={{ mb: 1.5, p: 1.25, borderRadius: 2, backgroundColor: "rgba(0, 51, 108, 0.045)" }}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                              <Typography sx={{ color: BRAND_BLUE, fontSize: "0.78rem", fontWeight: 800 }}>AI fit score</Typography>
                              <Chip label={`${prospect.score}/20`} size="small" sx={{ fontWeight: 800, color: BRAND_GREEN, backgroundColor: "rgba(100, 131, 21, 0.14)" }} />
                            </Stack>
                            {Array.isArray(prospect.match_reasons) && prospect.match_reasons.length ? (
                              <Typography sx={{ color: "#496581", fontSize: "0.72rem", lineHeight: 1.45, mt: 0.75 }}>
                                {prospect.match_reasons.slice(0, 2).join(" ")}
                              </Typography>
                            ) : (
                              <Typography sx={{ color: "#6b85a1", fontSize: "0.72rem", mt: 0.75 }}>
                                Score reflects available company, signal, and source evidence.
                              </Typography>
                            )}
                            {prospect.score_breakdown ? (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.9 }}>
                                {Object.entries(prospect.score_breakdown).filter(([, value]) => value?.points > 0).map(([key, value]) => (
                                  <Chip key={key} size="small" label={`${key.replace(/_/g, " ")}: ${value.points}`} sx={{ height: 19, fontSize: "0.64rem", backgroundColor: "#fff", color: BRAND_BLUE }} />
                                ))}
                              </Stack>
                            ) : null}
                          </Box>
                        ) : null}

                        {/* Meta details */}
                        <Stack spacing={0.75} sx={{ mb: 1.5 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <LocationOnIcon
                              sx={{ color: "text.disabled", fontSize: 15, flexShrink: 0 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: BRAND_BLUE, fontSize: "0.8rem" }}
                            >
                              {[prospect.city, prospect.state]
                                .filter(Boolean)
                                .join(", ") || "Location not available"}
                            </Typography>
                          </Stack>

                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <PeopleAltIcon
                              sx={{ color: "text.disabled", fontSize: 15, flexShrink: 0 }}
                            />
                            <Typography
                              variant="body2"
                              sx={{ color: BRAND_BLUE, fontSize: "0.8rem" }}
                            >
                              {prospect.number_of_employees != null
                                ? `${prospect.number_of_employees.toLocaleString()} employees`
                                : "Employee count not available"}
                            </Typography>
                          </Stack>

                          {prospect.matched_signal_type ? (
                            <Box sx={{ pt: 0.25 }}>
                              <Chip
                                icon={
                                  <CampaignIcon
                                    sx={{ fontSize: "14px !important" }}
                                  />
                                }
                                size="small"
                                label={prospect.matched_signal_type}
                                sx={{
                                  height: 22,
                                  backgroundColor: "rgba(100, 131, 21, 0.1)",
                                  color: BRAND_GREEN,
                                  fontWeight: 700,
                                  fontSize: "0.72rem",
                                  "& .MuiChip-icon": { color: BRAND_GREEN },
                                  maxWidth: "100%",
                                  "& .MuiChip-label": {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  },
                                }}
                              />
                            </Box>
                          ) : null}
                        </Stack>

                        {/* Signal summary */}
                        {prospect.signal_summary ? (
                          <Typography
                            variant="body2"
                            sx={{
                              color: BRAND_BLUE,
                              lineHeight: 1.5,
                              fontSize: "0.8rem",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mb: 1.5,
                            }}
                          >
                            {prospect.signal_summary}
                          </Typography>
                        ) : null}

                        {/* Footer */}
                        <Box sx={{ mt: "auto" }}>
                          <Divider sx={{ mb: 1.5, borderColor: "rgba(0,51,108,0.08)" }} />
                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                          >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              {prospect.website ? (
                                <Link
                                  href={prospect.website}
                                  target="_blank"
                                  rel="noreferrer"
                                  underline="hover"
                                  sx={{
                                    color: BRAND_BLUE,
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  <LanguageIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                                  {deriveDomain(prospect.website)}
                                </Link>
                              ) : null}
                              {prospect.signal_date ? (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "text.disabled", display: "block" }}
                                >
                                  {prospect.signal_date}
                                </Typography>
                              ) : null}
                              {prospect.source_url ? (
                                <Link
                                  href={prospect.source_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  underline="hover"
                                  sx={{
                                    color: BRAND_GREEN,
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.25,
                                  }}
                                >
                                  <OpenInNewIcon sx={{ fontSize: 11 }} />
                                  View source
                                </Link>
                              ) : null}
                            </Box>

                            {canAddToProspecting ? (
                              <Tooltip
                                title={isAdded ? "Added to prospecting" : "Add to prospect companies"}
                                arrow
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant={isAdded ? "contained" : "outlined"}
                                    startIcon={
                                      isAdding ? (
                                        <CircularProgress size={12} color="inherit" />
                                      ) : isAdded ? (
                                        <CheckIcon sx={{ fontSize: "14px !important" }} />
                                      ) : (
                                        <AddIcon sx={{ fontSize: "14px !important" }} />
                                      )
                                    }
                                    onClick={() =>
                                      !isAdded && handleAddProspect(prospect)
                                    }
                                    disabled={isAdding || isAdded}
                                    sx={{
                                      flexShrink: 0,
                                      borderRadius: 2,
                                      textTransform: "none",
                                      fontWeight: 700,
                                      fontSize: "0.75rem",
                                      height: 30,
                                      px: 1.25,
                                      ...(isAdded
                                        ? {
                                            backgroundColor: BRAND_GREEN,
                                            color: "#fff",
                                            borderColor: BRAND_GREEN,
                                            "&.Mui-disabled": {
                                              backgroundColor: BRAND_GREEN,
                                              color: "#fff",
                                              opacity: 0.9,
                                            },
                                          }
                                        : {
                                            borderColor: BRAND_GREEN,
                                            color: BRAND_GREEN,
                                            "&:hover": {
                                              backgroundColor: "rgba(100,131,21,0.08)",
                                              borderColor: BRAND_GREEN,
                                            },
                                          }),
                                    }}
                                  >
                                    {isAdded ? "Added" : "Add"}
                                  </Button>
                                </span>
                              </Tooltip>
                            ) : null}
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
}
